import React, { useCallback, useState } from 'react';
import ImportOptions from './ImportOptions';
import CategoriesTab from './CategoriesTab';
import SeriesSection from './SeriesSection';
import PageDimmer from './PageDimmer';
import PasteTooltip from './PasteTooltip';
import { onUpload } from './../utils/dragAndDropHelper';
import { UnknownParamsFunction } from 'shared/types/commonPropTypes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { getAllowProps, getIsLiveDataSource, isUsingChartType } from '../utils/seriesHelper';
import LiveSourceModal from 'shared/editor/LiveSourceModal';
import { setAction } from 'redux/actions/projectConfig';
import { setDataMap } from '../utils/dataGridHelper';
import PointMapSearch from '../editor/PointMapSearch';
import ExportOptions from './ExportOptions';

type DataLeftSectionProps = {
  isWordcloud: boolean;
  children?: React.ReactNode;
  isTable?: boolean;
  inEditor?: boolean;
};

const DataLeftSection = (props: DataLeftSectionProps) => {
  const dispatch = useDispatch();
  const [showLiveSourceModal, setShowLiveSourceModal] = useState(false);
  const { seriesAssigns, aggregatedOptions, dataConfig, isPastingActive } = useSelector(
    (state: RootState) => state.projectConfig
  );
  const { type } = useSelector((state: RootState) => state.layoutEditorPage);
  const { isMap, isThemeEditor } = useSelector((state: RootState) => state.chartEditorPage);
  const { allowAddSeries, seriesTypes } = getAllowProps(aggregatedOptions, props.isTable);

  let mouseLeaveTimeout: NodeJS.Timeout;
  const isPointMap = seriesTypes.includes('mappoint');
  const isBubbleMap = seriesTypes.includes('mapbubble');
  const isPackedBubble = isUsingChartType(aggregatedOptions, 'packedbubble');
  const isChart = type === 'chart';
  const isLiveDataSource = getIsLiveDataSource(aggregatedOptions, dataConfig);
  const getIndexForAssignRawOptions = isPointMap ? 1 : 0;

  const onMouseLeave = useCallback(() => {
    mouseLeaveTimeout = setTimeout(() => {
      dispatch(
        setAction({
          activeColumns: {
            columns: [],
            series: []
          }
        })
      );
    }, 100);
  }, []);

  const onMouseEnter = useCallback((index: number[], series: number[]) => {
    if (mouseLeaveTimeout) clearTimeout(mouseLeaveTimeout);
    dispatch(
      setAction({
        activeColumns: {
          columns: index,
          series: series
        }
      })
    );
  }, []);

  const toggleIsPastingActive = (toggle?: boolean) => {
    dispatch(
      setAction({
        isPastingActive: toggle ?? !isPastingActive
      })
    );
  };

  const closeModal = () => {
    setShowLiveSourceModal(false);
  };

  const setData = (options: unknown, dataType: string, cb?: UnknownParamsFunction | null, skipDataHelper?: boolean) => {
    dispatch(setDataMap[type]({ options, dataType, cb, skipDataHelper }));
  };

  const onHasData = (options: unknown, dataType: string) => {
    setData(options, dataType, closeModal);
  };

  const onClickUpload = () => onUpload(setData, isMap);
  const parentSelector = () => document.querySelector('.page-container');

  return (
    <div className={`grid gap-2 ${props.inEditor ? 'ev-sm:w-[332px]' : 'ev-md:mr-2'}`}>
      <LiveSourceModal
        isOpen={showLiveSourceModal}
        parentSelector={parentSelector}
        close={closeModal}
        onHasData={onHasData}
        isChart={isChart}
      />
      {isPointMap && <PointMapSearch />}
      {isPastingActive && (
        <>
          <PasteTooltip onClick={() => toggleIsPastingActive()} />
          <PageDimmer onClick={() => toggleIsPastingActive()} />
        </>
      )}

      {!isLiveDataSource && !props.isWordcloud && (
        <>
          <CategoriesTab
            setActiveColumns={onMouseEnter}
            assignOption={seriesAssigns[getIndexForAssignRawOptions]}
            onMouseLeave={onMouseLeave}
            isMap={isMap}
            seriesAssignsIndex={getIndexForAssignRawOptions}
          />
          <SeriesSection
            setActiveColumns={onMouseEnter}
            onMouseLeave={onMouseLeave}
            isMap={isMap}
            allowAddSeries={allowAddSeries}
            isAlternativeMapping={isMap && (isBubbleMap || isPointMap)}
            type={type}
            seriesTypes={seriesTypes}
          />
        </>
      )}

      {props.inEditor && <hr className="border-ev-dark-purple my-4" />}

      <div className="flex flex-col gap-2">
        {!props.isWordcloud && (
          <ImportOptions
            isChart={isChart}
            isMap={isMap}
            togglePasting={toggleIsPastingActive}
            setShowLiveSourceModal={setShowLiveSourceModal}
            isPackedBubble={isPackedBubble}
            onClickUpload={onClickUpload}
            aggregatedOptions={aggregatedOptions}
            dataConfig={dataConfig}
            isLiveDataSource={isLiveDataSource}
          />
        )}

        {props.inEditor && !isThemeEditor && <ExportOptions />}
      </div>
    </div>
  );
};

export default DataLeftSection;
